// Kasse ohne Verbindung – feste Werte an genau einer Stelle (docs/kasse-offline/SPEZIFIKATION.md §17). Rein, für
// Server, Gerät und Oberfläche. Was ein Betrieb einstellen darf, steht in pos_offline_setting (Vorgaben und Bereiche
// unten, §11.1); alles andere ist hier fest. Offline ist ein Störfallprozess (§2.4): Die Grenzen sind bewusst kurz.
//
// Zeiten in Millisekunden, Geld in Cent, Größen in Bytes (UTF-8).

// ---- Verbindungserkennung und Herzschlag (§10.1) -------------------------------------------------------------------
/** Gesundheitsprüfung GET /api/health im Zustand online */
export const HEALTH_INTERVAL_MS = 15_000
/** Zeitlimit je Gesundheitsprüfung (AbortSignal.timeout) */
export const HEALTH_TIMEOUT_MS = 3_000
/** Gesundheitsprüfung in pruefen und stoerung */
export const HEALTH_RETRY_MS = 5_000
/** Gesundheitsprüfung ohne Verbindung: 5 s → 10 s → 30 s, danach alle 30 s */
export const HEALTH_BACKOFF_MS: readonly number[] = [5_000, 10_000, 30_000]
/** so viele erfolgreiche Gesundheitsprüfungen in Folge (plus ein Herzschlag) führen von offline nach rueckkehr */
export const HEALTH_OK_FOR_RETURN = 2
/** Karenz: so lange scheitert die Gesundheitsprüfung auf Netzebene, bevor die Kasse nach offline wechselt */
export const OFFLINE_GRACE_MS = 30_000
/** stoerung länger als das → gesperrt mit Papier-Notbetrieb */
export const STOERUNG_MAX_MS = 180_000
/** Wiederholung eines Kassenaufrufs in stoerung: 2 s · 2^n … */
export const STOERUNG_RETRY_MS = 2_000
/** … höchstens 10 s */
export const STOERUNG_RETRY_MAX_MS = 10_000
/** Herzschlag pos.till.heartbeat online, dazu nach jedem Zustandswechsel */
export const HEARTBEAT_INTERVAL_MS = 60_000

// ---- Zeitlimits der Kassenaufrufe einer Offline-Kasse (§10.1) -------------------------------------------------------
/** pos.sale.edit und alle übrigen Kassenaufrufe je Versuch */
export const EDIT_TIMEOUT_MS = 8_000
/** pos.sale.complete je Versuch (deutlich über den rund 10 s, die der Server im Ausfall der Einheit braucht) */
export const COMPLETE_TIMEOUT_MS = 20_000
/** Zeitlimit von loadSession in der Hülle (§14.7) */
export const SESSION_TIMEOUT_MS = 5_000

// ---- Übergänge, Nachweis, Übertragung (§7.2, §10.2, §14.1) ---------------------------------------------------------
/** nach der Wiederverbindung: offline begonnene und lokal geparkte Bons binnen dieser Frist erledigen (T4) */
export const RETURN_FINISH_MS = 300_000
/** Zeitfenster des Geräte-Nachweises: `at` ± so viel um die Serverzeit */
export const PROOF_WINDOW_MS = 300_000
/** Stapel der Übertragung: höchstens so viele Sätze (Grenze des Vertrags pos.offline.submit) … */
export const UPLOAD_BATCH = 25
/**
 * … davon sendet das Gerät (und das Einlesen einer Datei) je Aufruf so viele: Ein Stapel ist eine Transaktion; sie hält
 * Nummernkreis und Bestandszeilen bis zum Ende. Mit 25 Sätzen warteten gleichzeitige Online-Bons der Filiale im
 * 95. Perzentil 2,3 s, mit 10 Sätzen 0,7 s – und die Übertragung war insgesamt schneller (§18.4, gemessen 20.10.2026,
 * e2e/pos-offline/load.spec.ts).
 */
export const UPLOAD_BATCH_DEVICE = 10
/** … und zusammen höchstens so viele Bytes payload (die Rumpfgrenze des Servers ist 1 MB) */
export const UPLOAD_BATCH_BYTES = 524_288
/** Wiederholung der Übertragung: 1 s · 2^n … */
export const UPLOAD_BACKOFF_BASE_MS = 1_000
/** … höchstens 5 min … */
export const UPLOAD_BACKOFF_MAX_MS = 300_000
/** … mit ± 20 % Zufall */
export const UPLOAD_JITTER = 0.2
/** nach so vielen gleichen Fehlschlägen (server) wird der Stapel halbiert, bei einem Satz „Übertragung stockt“ */
export const UPLOAD_SAME_FAILURES = 3
/** „Übertragung stockt“: weiter alle 5 min versuchen */
export const UPLOAD_STUCK_RETRY_MS = 300_000
/** Grenze eines Satzes in UTF-8-Bytes (das Gerät prüft vor dem Abschluss des Bons, §9.1) */
export const ENTRY_MAX_BYTES = 262_144
/** grobe Grenze je Satz im Vertrag von pos.offline.submit in Zeichen (die Bytes prüft der Handler je Satz) */
export const SUBMIT_PAYLOAD_MAX_CHARS = 400_000
/** Rohsätze in Quarantäne dürfen bis zur Rumpfgrenze des Servers groß sein (pos_offline_entry_size_check) */
export const QUARANTINE_MAX_BYTES = 1_048_576
/** Texte im Offline-Satz höchstens so viele Zeichen … */
export const ENTRY_TEXT_MAX = 300
/** … Artikeltexte der Aufzeichnung höchstens so viele (wie RecordLine.text) */
export const ENTRY_ITEM_TEXT_MAX = 200

// ---- Uhr (§9.5, §9.4) -------------------------------------------------------------------------------------------------
/** Vorlauf von Date.now() gegenüber der Serverzeit-Schätzung: ab so viel nur Hinweis (flag = 'drift') */
export const CLOCK_DRIFT_WARN_MS = 120_000
/** Rücksprung der Geräteuhr um mehr als so viel → gesperrt */
export const CLOCK_BACK_BLOCK_MS = 120_000
/** Server: createdAt darf so weit vor dem vorigen Satz liegen (sonst clock_hint) */
export const CLOCK_PREV_TOLERANCE_MS = 120_000
/** Server: createdAt darf so weit nach dem Eingang liegen (sonst clock_hint) */
export const CLOCK_FUTURE_TOLERANCE_MS = 300_000
/** Server: createdAt darf so weit vor dem Schichtbeginn liegen (sonst clock_hint) */
export const CLOCK_SHIFT_TOLERANCE_MS = 300_000

// ---- Bedienung, Speicher, Aufbewahrung auf dem Gerät (§9.1, §10.6, §11.2) -------------------------------------------
/** dieselbe Anmeldung länger als 4 h ohne Verbindung → Hinweis und Satz note operator_long */
export const OPERATOR_HINT_AFTER_MS = 14_400_000
/** freier Speicher unter 50 MiB → gesperrt */
export const STORAGE_MIN_FREE_BYTES = 52_428_800
/** freier Speicher unter 100 MiB → Hinweis */
export const STORAGE_WARN_FREE_BYTES = 104_857_600
/** bestätigte Sätze frühestens nach 7 Tagen lokal löschen … */
export const KEEP_CONFIRMED_DAYS = 7
/** … und höchstens so viele bestätigte Sätze behalten (der Satz mit der höchsten Folgenummer bleibt immer) */
export const KEEP_CONFIRMED_MAX = 1_000
/** Ring der online beantworteten Vorgänge seit dem letzten Startpaket (Soll-Bestand, §11.6) */
export const RECENT_MAX = 500
/** Ring der technischen Ereignisse für die Fehlersuche */
export const LOG_MAX = 5_000
/** Warnstufe 3: ab diesem Anteil einer Grenze (Dauer, Anzahl, Speicher) in Basispunkten */
export const WARN_LEVEL3_BP = 7_500

// ---- Server: Wächter, Pakete, Nummern, Papier (§5.1, §7.4, §11.5, §14.4) -------------------------------------------
/** Wächter: Gerät mit offener Schicht schweigt länger als 3 min → Mitteilung pos.offline.silent */
export const WATCHDOG_SILENT_MS = 180_000
/** Wächter: Sätze warten länger als 24 h → Mitteilung „Offline-Bons seit … nicht übertragen“ */
export const WATCHDOG_STALE_QUEUE_MS = 86_400_000
/** Obergrenze des Stammdaten-Pakets je Standort (Gebinde) */
export const SNAPSHOT_MAX_UNITS = 50_000
/** nicht referenzierte Paket-Inhalte leert der Aufräumlauf nach so vielen Tagen */
export const SNAPSHOT_CONTENT_DAYS = 30
/** gebaute Pakete hält der Prozess so lange im Speicher (höchstens ein Bau je Standort und Herzschlag-Takt) */
export const SNAPSHOT_CACHE_MS = 60_000
/** Reservierung nach der Rückkehr: höchstens max_vorgaenge + so viele Nummern (T1-Abbrüche) */
export const RESERVE_SLACK = 10
/** Vordruck-Reserve je gekoppeltem Gerät (Blattnummern im Startpaket) */
export const PAPER_DEVICE_RESERVE = 5
/** Vorrat an Vordrucken je Kasse nach Kassieranweisung */
export const PAPER_STOCK_DEFAULT = 10
/** pos.paper.forms.issue: höchstens so viele Blätter je Aufruf (Zweck print) */
export const PAPER_ISSUE_MAX = 50
/** Zeilen je Papierblatt (pos_paper_sheet_lines_check) */
export const PAPER_SHEET_MAX_LINES = 500
/** Mindestlänge einer Begründung bzw. Notiz bei Erledigungen, die eine verlangen (§10.5) */
export const RESOLUTION_NOTE_MIN = 10
/** Fingerabdruck des Geräteschlüssels in Audit und Anzeige: so viele Zeichen */
export const FINGERPRINT_SHORT = 16

// ---- Formate (§5, §9.2) -----------------------------------------------------------------------------------------------
/** Fassung des Offline-Satzes (modules/pos/lib/offline/format.ts) */
export const OFFLINE_FORMAT = 1
/** Fassung des Stammdaten-Pakets (modules/pos/lib/offline/types.ts) */
export const SNAPSHOT_FORMAT = 1
/** Fassung des Startpakets (modules/pos/lib/offline/types.ts) */
export const STARTPACK_FORMAT = 1
/** Fassung der Datei „Offline-Daten sichern“ (§11.4, format.ts) */
export const OFFLINE_BACKUP_FORMAT = 1
/** Kettenwert vor dem ersten Satz eines Geräts (pos_till_device.confirmed_hash beim Koppeln) */
export const ZERO_HASH = '0'.repeat(64)

// ---- Feste Freigaben – umgestellt erst nach der jeweiligen Antwort der Steuerkanzlei (§20) ---------------------------
/** Offline-fähiges Startpaket auch an Kassen im Echtbetrieb – erst nach Antwort auf F1, F2, F4, F14 (O4) */
export const OFFLINE_LIVE_ALLOWED = false
/** Merxo-Kante meldet die Anwendung gestört (edge) → nach der Karenz offline statt stoerung – erst nach F1 (§10.1) */
export const OFFLINE_ON_SERVER_OUTAGE = false
/** T2: spätes Beenden mit identischen Vorgangsdaten statt Abbruch – erst nach F3 (§8.4) */
export const T2_LATE_FINISH = false
/** Link bzw. QR-Code zum elektronischen Beleg auf dem Offline-Bon – erst nach F9 (§8.2) */
export const OFFLINE_RECEIPT_LINK = false

// ---- Einstellungen je Betrieb: Vorgaben und Bereiche (§11.1, pos_offline_setting) -----------------------------------
export interface OfflineLimits {
  /** Dauer seit dem letzten Serverkontakt in Minuten */
  maxOfflineMinutes: number
  /** ohne Verbindung vergebene Vorgangsnummern je Episode */
  maxVorgaenge: number
  /** Betrag je Bon */
  maxBonCents: number
  /** zurückgenommener Wert brutto je Bon (auch im gemischten Bon) */
  maxEmptiesCents: number
  /** Nettoauszahlung je Bon */
  maxPayoutCents: number
  /** Wechselgeld vom Tresor je Vorgang */
  maxCashInCents: number
  /** Hinweis „bitte beheben“ ab so vielen Minuten (Warnstufe 2) */
  warnAfterMinutes: number
  /** Belegnummer des Terminals bei Karte Pflicht */
  cardReferenceRequired: boolean
  /** Klärfall „Ausfall dokumentieren“ sperrt den Abschluss */
  outageDocBlocking: boolean
}

/** Vorgaben (= Standardwerte der Tabelle) */
export const OFFLINE_LIMIT_DEFAULTS: Readonly<OfflineLimits> = Object.freeze({
  maxOfflineMinutes: 720,
  maxVorgaenge: 1_000,
  maxBonCents: 50_000,
  maxEmptiesCents: 15_000,
  maxPayoutCents: 10_000,
  maxCashInCents: 20_000,
  warnAfterMinutes: 60,
  cardReferenceRequired: true,
  outageDocBlocking: false,
})

/**
 * Feste Bereiche (pos_offline_setting_*_check). Abhängige Obergrenzen: maxEmptiesCents ≤ maxBonCents,
 * maxPayoutCents ≤ maxEmptiesCents, warnAfterMinutes ≤ maxOfflineMinutes.
 */
export const OFFLINE_LIMIT_RANGES = Object.freeze({
  maxOfflineMinutes: { min: 30, max: 1_440 },
  maxVorgaenge: { min: 50, max: 5_000 },
  maxBonCents: { min: 1_000, max: 1_000_000 },
  maxEmptiesCents: { min: 0 },
  maxPayoutCents: { min: 0 },
  maxCashInCents: { min: 0, max: 200_000 },
  warnAfterMinutes: { min: 5 },
} as const)

/** Kanzlei, die der Demo-Seed einträgt; außerhalb von Demo-Mandanten lehnt pos.offline.settings.update sie ab */
export const DEMO_APPROVAL_FIRM = 'Demo-Betrieb'

/**
 * Meint die Angabe der Freigabe die Demo-Freigabe? Schreibweise egal – Groß/Klein, Bindestrich, Leerzeichen,
 * Satzzeichen, Unicode-Varianten: „Demo-Betrieb“, „Demo Betrieb“, „Demobetrieb“, „Demo-Betrieb.“, „DEMO“, „Demo GmbH“.
 * Außerhalb von Demo-Mandanten abgelehnt (§11.1; Befund G-9 der Gegenprüfung).
 */
export function isDemoApproval(firm: string): boolean {
  const words = firm.normalize('NFKC').toLocaleLowerCase('de-DE').split(/[^\p{L}\p{N}]+/u).filter(Boolean)
  const joined = words.join('')
  return words.includes('demo') || joined === 'demo' || joined.includes('demobetrieb')
}
/** Mindestlänge des Namens der Kanzlei (pos_offline_setting_approval_check) */
export const APPROVAL_FIRM_MIN = 3
